import React, { useEffect, useState, useRef, useCallback } from 'react';
import CoachMessageInput, { useCoachComposerSubmit } from './CoachMessageInput';
import './StrategyModal.css';
import PlanEditor from './PlanEditor';
import RestDaysSettings from './RestDaysSettings';
import TravelReplacementSettings from './TravelReplacementSettings';
import StrategyRoadmap from './StrategyRoadmap';
import ModalLoading from './ModalLoading';
import { getDojoStrategyAccess, getStrategyContextState } from '../utils/dojoStrategyAccess';
import { resolveStrategyAcceptanceChoice } from '../utils/strategyAcceptancePolicy';
import { getCanonicalWorkoutForToday, getCanonicalWorkoutWeekday } from '../utils/strategyPlanDay';
import { getStrategyTimelinePresentation } from '../utils/strategyTimelinePresentation';
import { shouldAutoStartStrategyTransition } from './strategyTransitionAction';
import { strategyGenerationProgressLabel } from './strategyGenerationProgressLabel';

const StrategyModal = ({ backendService, modalService, close, embedded = false, onInitialReady, startNewOnOpen = false }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [activeStrategy, setActiveStrategy] = useState(null);
    const [tab, setTab] = useState('current'); // 'current' | 'history'
    const [allStrategies, setAllStrategies] = useState([]);
    const [viewingStrategy, setViewingStrategy] = useState(null);
    
    // Negotiation state
    const [negotiating, setNegotiating] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [sending, setSending] = useState(false);
    const [draftStrategy, setDraftStrategy] = useState(null);
    const [progressSteps, setProgressSteps] = useState([]);
    const [, setProgressJobId] = useState(null);
    const transitionStartedRef = useRef(false);
    
    // Review state
    const [reviewing, setReviewing] = useState(false);
    const [reviewResult, setReviewResult] = useState(null);
    const [reviewInput, setReviewInput] = useState('');

    // Pending plan-shape changes from a review (additions/removals of
    // meal entries, workout plan changes that need regenerate). Tracks
    // applying state per scope so the UI can disable buttons mid-flight.
    const [pendingChangesState, setPendingChangesState] = useState({
        applying: false,
        applied_scopes: [],
        error: null
    });
    
    // Current plan id (resolved from user affiliation)
    const [currentPlanId, setCurrentPlanId] = useState(null);
    const [dojoPlanReadOnly, setDojoPlanReadOnly] = useState(true);
    const [inDojo, setInDojo] = useState(false);
    const [dojoAuthorityName, setDojoAuthorityName] = useState(null);
    const [authorityLoading, setAuthorityLoading] = useState(true);
    const [authorityError, setAuthorityError] = useState(false);
    const [regeneratingPlan, setRegeneratingPlan] = useState(false);

    // Archived plan snapshots (created when an accepted strategy replaces
    // the user's previous plan). Lets the user restore an old plan if the
    // new strategy doesn't work out.
    const [archivedPlans, setArchivedPlans] = useState([]);
    const [, setArchivedPlansLoading] = useState(false);
    const [restoringPlanId, setRestoringPlanId] = useState(null);
    // Lightweight summary of the user's currently-active plan so we can
    // surface it at the top of the History tab's saved-plans list with the
    // same green "active" treatment as the active strategy.
    const [activePlanSummary, setActivePlanSummary] = useState(null);
    const [currentPlanEntries, setCurrentPlanEntries] = useState([]);
    const [planEntriesResolved, setPlanEntriesResolved] = useState(false);
    // Read-only "what was in this plan" view, keyed by plan _id.
    // Value: { loading, error, entries }
    const [archivedPlanDetails, setArchivedPlanDetails] = useState({});
    const [expandedArchivedPlanId, setExpandedArchivedPlanId] = useState(null);
    // Bumped to force the PlanEditor to remount + refetch after a restore.
    const [planEditorKey, setPlanEditorKey] = useState(0);
    
    const chatEndRef = useRef(null);
    const prevMessageCountRef = useRef(0);

    // Auto-scroll the chat — but only when a NEW user message was just sent.
    // Scrolling on every AI/progress update yanks the user away from the
    // strategy preview they're trying to read while typing a follow-up.
    useEffect(() => {
        const prev = prevMessageCountRef.current;
        const next = messages.length;
        prevMessageCountRef.current = next;
        if (next <= prev) return;
        const lastMsg = messages[next - 1];
        if (!lastMsg || lastMsg.role !== 'user') return;
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [messages]);

    const [initialPlanReady, setInitialPlanReady] = useState(false);
    const markInitialPlanReady = useCallback(() => setInitialPlanReady(true), []);
    useEffect(() => {
        // Only the current active strategy mounts an editor. Never wait for an
        // inactive tab or reset the outer form during later plan operations.
        if (!loading && !authorityLoading &&
            (loadError || !activeStrategy || !currentPlanId || initialPlanReady)) onInitialReady?.();
    }, [loading, authorityLoading, loadError, activeStrategy, currentPlanId, initialPlanReady, onInitialReady]);

    // Load active strategy on mount
    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError(null);
        backendService.fetchActiveStrategy().then(strategy => {
            if (active) {
                setActiveStrategy(strategy);
            }
        }).catch(error => {
            if (active) setLoadError(error.message || 'Could not load strategy. Close and try again.');
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [backendService]);

    // Resolve the user's current plan id
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const dojo = await backendService.getMyDojo();
                const affiliation = await backendService.fetchUserAffiliation().catch(() => null);
                if (active) {
                    const access = getDojoStrategyAccess(dojo);
                    setCurrentPlanId(access.inDojo ? (dojo?.workout_plan_id || null) : (affiliation?.activity_plan_id || null));
                    setInDojo(access.inDojo);
                    setDojoPlanReadOnly(access.readOnly);
                    setDojoAuthorityName(access.dojoName);
                    setAuthorityError(false);
                }
            } catch (e) {
                if (active) {
                    setCurrentPlanId(null);
                    setInDojo(false);
                    setDojoPlanReadOnly(true);
                    setAuthorityError(true);
                }
            } finally {
                if (active) setAuthorityLoading(false);
            }
        })();
        return () => { active = false; };
    }, [backendService]);

    const openDojoSettings = useCallback(async () => {
        if (!modalService?.open) return;
        const { default: DojoSettingsModal } = await import('./DojoSettingsModal');
        close?.();
        modalService.open(DojoSettingsModal, { backendService });
    }, [backendService, close, modalService]);

    // Fetch a lightweight summary of the active plan so the History tab
    // can show it as a "currently active" card at the top of the list.
    useEffect(() => {
        if (!currentPlanId) {
            setActivePlanSummary(null);
            setCurrentPlanEntries([]);
            setPlanEntriesResolved(true);
            return;
        }
        let active = true;
        setPlanEntriesResolved(false);
        (async () => {
            try {
                const p = await backendService.fetchPlanDetails(currentPlanId);
                let entries = [];
                try {
                    entries = await backendService.fetchPlanItems(currentPlanId);
                } catch {
                    // Keep the detail response as a degraded fallback, but do
                    // not prefer its raw entries over the enriched entry route.
                    entries = Array.isArray(p?.entries) ? p.entries : [];
                }
                if (active) {
                    setActivePlanSummary(p || null);
                    setCurrentPlanEntries(Array.isArray(entries) ? entries : []);
                    setPlanEntriesResolved(true);
                }
            } catch {
                if (active) {
                    setActivePlanSummary(null);
                    setCurrentPlanEntries([]);
                    setPlanEntriesResolved(true);
                }
            }
        })();
        return () => { active = false; };
    }, [backendService, currentPlanId, planEditorKey]);

    // Load history when switching to history tab
    useEffect(() => {
        if (tab === 'history' && allStrategies.length === 0) {
            backendService.fetchAllStrategies().then(setAllStrategies);
        }
    }, [tab, backendService, allStrategies.length]);

    // ─── Start New Strategy Negotiation ───
    const startNewStrategy = useCallback(async () => {
        setNegotiating(true);
        setSending(true);
        setMessages([]);
        setConversationId(null);
        setDraftStrategy(null);
        setProgressSteps([]);
        setProgressJobId(null);

        try {
            const result = await backendService.generateStrategy({
                onProgress: (job) => {
                    if (!job) return;
                    if (job._id) setProgressJobId(String(job._id));
                    const steps = job?.progress?.steps;
                    if (Array.isArray(steps)) setProgressSteps(steps);
                }
            });
            if (result) {
                setConversationId(result.conversation_id || String(result._id));
                setDraftStrategy(result);
                
                const d = result.data || {};
                let aiMessage = d.ai_message;
                // The full strategy is rendered as a live preview below the
                // chat, so the chat message itself stays brief.
                if (!aiMessage) {
                    aiMessage = d.primary_goal
                        ? `Here's a draft strategy for **${d.primary_goal}**. Review it below — what would you like to adjust?`
                        : `I've analyzed your training history and profile. Let me propose a strategy for you...`;
                }
                
                setMessages([{ role: 'ai', text: aiMessage }]);
            }
        } catch (err) {
            console.error('Failed to start strategy:', err);
            setMessages([{ role: 'ai', text: 'Sorry, I had trouble generating a strategy. Please try again.' }]);
        }
        setSending(false);
        setProgressSteps([]);
    }, [backendService]);

    // A completed-strategy CTA should lead directly to the next phased-plan
    // proposal, rather than opening the expired overview and requiring the user
    // to rediscover the generic New Strategy button. Authority must resolve
    // first so dojo members never start a chief-owned generation job.
    useEffect(() => {
        if (!shouldAutoStartStrategyTransition({
            requested: startNewOnOpen,
            loading,
            authorityLoading,
            readOnly: dojoPlanReadOnly,
            started: transitionStartedRef.current
        })) return;
        transitionStartedRef.current = true;
        startNewStrategy();
    }, [startNewOnOpen, loading, authorityLoading, dojoPlanReadOnly, startNewStrategy]);

    // ─── Send a negotiation message ───
    const sendMessage = useCallback(async () => {
        if (!inputValue.trim() || sending) return;
        
        const userMsg = inputValue.trim();
        setInputValue('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setSending(true);
        setProgressSteps([]);

        try {
            const result = await backendService.generateStrategy({
                user_message: userMsg,
                conversation_id: conversationId,
                onProgress: (job) => {
                    if (!job) return;
                    if (job._id) setProgressJobId(String(job._id));
                    const steps = job?.progress?.steps;
                    if (Array.isArray(steps)) setProgressSteps(steps);
                }
            });
            
            if (result) {
                setConversationId(result.conversation_id || String(result._id));
                setDraftStrategy(result);
                
                const d = result.data || {};
                const aiMessage = d.ai_message || 
                    `Updated strategy: **${d.primary_goal || 'Your Strategy'}**\n${d.summary || ''}\n\nDoes this look good? You can accept it or ask for more changes.`;
                
                setMessages(prev => [...prev, { role: 'ai', text: aiMessage }]);
            }
        } catch (err) {
            console.error('Failed to continue negotiation:', err);
            setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, something went wrong. Try again?' }]);
        }
        setSending(false);
        setProgressSteps([]);
    }, [inputValue, sending, conversationId, backendService]);

    // ─── Accept/Activate Draft (atomic: activates strategy AND generates plan) ───
    const acceptStrategy = useCallback(async () => {
        if (!draftStrategy?._id) return;

        // If the user already has a plan with workouts, ask before overwriting.
        // First-time users (no plan yet) get the new plan auto-generated.
        let replaceExisting = true;
        if (currentPlanId) {
          const confirmed = window.confirm(
                inDojo
                    ? 'Activate this dojo Strategy & Plan and replace the dojo workouts with its generated programming?\n\nCancel leaves the current dojo strategy and workouts unchanged.'
                    : 'Replace your current workouts with the AI-generated plan from this strategy?\n\nYour existing plan structure will be replaced with sessions tailored to the strategy\'s current phase.'
            );
            const choice = resolveStrategyAcceptanceChoice({ inDojo, hasPlan: true, confirmed });
            if (!choice.proceed) return;
            replaceExisting = choice.replaceExisting;
        }

        setSending(true);
        // Re-use the chat timeline to surface acceptance progress.
        setProgressSteps([]);
        setMessages(prev => [...prev, { role: 'ai', text: replaceExisting
            ? 'Activating your strategy and building your workouts…'
            : 'Activating your strategy (keeping your existing workouts)…'
        }]);

        try {
            const result = await backendService.acceptStrategy(String(draftStrategy._id), {
                replace_existing: replaceExisting,
                onProgress: (job) => {
                    if (!job) return;
                    if (job._id) setProgressJobId(String(job._id));
                    const steps = job?.progress?.steps;
                    if (Array.isArray(steps)) setProgressSteps(steps);
                }
            });

            if (result?.strategy) {
                setActiveStrategy(result.strategy);
                if (result.plan_id) setCurrentPlanId(result.plan_id);
                setNegotiating(false);
                setDraftStrategy(null);
                setMessages([]);
                setProgressSteps([]);
                setTab('current');
            }
        } catch (err) {
            console.error('Failed to accept strategy:', err);
            setMessages(prev => [...prev, { role: 'ai', text: `Sorry — something went wrong activating your strategy: ${err.message || 'unknown error'}. Try again?` }]);
        }
        setSending(false);
    }, [draftStrategy, currentPlanId, inDojo, backendService]);

    // ─── Review Active Strategy ───
    const requestReview = useCallback(async () => {
        if (!activeStrategy?._id) return;
        setReviewing(true);
        setReviewResult(null);
        setPendingChangesState({ applying: false, applied_scopes: [], error: null });

        try {
            const result = await backendService.reviewStrategy(
                String(activeStrategy._id),
                reviewInput.trim() || undefined
            );
            if (result) {
                setReviewResult(result.review);
                const refreshed = await backendService.fetchActiveStrategy();
                setActiveStrategy(refreshed);
            }
        } catch (err) {
            console.error('Failed to review strategy:', err);
        }
        setReviewing(false);
    }, [activeStrategy, reviewInput, backendService]);

    // ─── Archive Strategy ───
    const archiveStrategy = useCallback(async () => {
        if (!activeStrategy?._id) return;
        const confirmed = window.confirm('Archive this strategy? It will no longer guide your daily recommendations.');
        if (!confirmed) return;
        
        try {
            await backendService.archiveStrategy(String(activeStrategy._id));
            setActiveStrategy(null);
            setTab('current');
        } catch (err) {
            console.error('Failed to archive strategy:', err);
        }
    }, [activeStrategy, backendService]);

    // ─── Regenerate Plan (strategy-aware) ───
    const regeneratePlan = useCallback(async () => {
        if (!currentPlanId) return;
        const confirmed = window.confirm(
            activeStrategy
                ? 'Regenerate your plan based on the current strategy, phase, and milestone progress? This will overwrite your current workout templates.'
                : 'Regenerate your plan? This will overwrite your current workout templates.'
        );
        if (!confirmed) return;

        setRegeneratingPlan(true);
        try {
            const strategyId = activeStrategy ? String(activeStrategy._id) : null;
            await backendService.refreshWorkouts(currentPlanId, strategyId);
        } catch (err) {
            console.error('Failed to regenerate plan:', err);
        }
        setRegeneratingPlan(false);
    }, [currentPlanId, activeStrategy, backendService]);

    // ─── Archived plans: load, restore, delete ───
    const loadArchivedPlans = useCallback(async () => {
        setArchivedPlansLoading(true);
        try {
            const list = await backendService.fetchArchivedPlans();
            setArchivedPlans(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Failed to fetch archived plans:', err);
        }
        setArchivedPlansLoading(false);
    }, [backendService]);

    useEffect(() => {
        loadArchivedPlans();
    }, [loadArchivedPlans, activeStrategy]);

    const restoreArchivedPlan = useCallback(async (planId) => {
        const confirmed = window.confirm(
            'Restore this archived plan?\n\n' +
            'Your current workouts will be archived first, then replaced with this saved plan. ' +
            'You can restore again later if you change your mind.'
        );
        if (!confirmed) return;
        setRestoringPlanId(planId);
        try {
            await backendService.restoreArchivedPlan(planId);
            await loadArchivedPlans();
            // Force PlanEditor to remount so it re-fetches the (now restored) plan.
            setPlanEditorKey(k => k + 1);
        } catch (err) {
            console.error('Failed to restore archived plan:', err);
            window.alert('Sorry, restoring this plan failed. Please try again.');
        }
        setRestoringPlanId(null);
    }, [backendService, loadArchivedPlans]);

    const deleteArchivedPlan = useCallback(async (planId) => {
        const confirmed = window.confirm('Permanently delete this archived plan? This cannot be undone.');
        if (!confirmed) return;
        try {
            await backendService.deleteArchivedPlan(planId);
            await loadArchivedPlans();
        } catch (err) {
            console.error('Failed to delete archived plan:', err);
        }
    }, [backendService, loadArchivedPlans]);

    // ─── Restore an archived strategy ───
    // Reactivates an old strategy and (optionally) rebuilds the plan from
    // it. Mirrors the restore-plan UX so users have one mental model for
    // rolling back. Re-uses the same acceptance job the new-strategy
    // accept flow uses on the backend.
    const [restoringStrategyId, setRestoringStrategyId] = useState(null);
    const restoreStrategy = useCallback(async (strategy) => {
        if (!strategy?._id) return;
        const goal = strategy.data?.primary_goal || 'this strategy';
        let replaceExisting = true;
        if (currentPlanId) {
            const confirmed = window.confirm(
                `Restore "${goal}"?\n\n` +
                (inDojo
                    ? 'The dojo strategy and workouts will both be rebuilt from this version. Cancel leaves both unchanged.'
                    : 'Your current strategy will be archived and your workouts will be rebuilt from this one. Cancel keeps the existing workouts while reactivating only the strategy.')
            );
            const choice = resolveStrategyAcceptanceChoice({ inDojo, hasPlan: true, confirmed });
            if (!choice.proceed) return;
            replaceExisting = choice.replaceExisting;
        }
        setRestoringStrategyId(String(strategy._id));
        try {
            const result = await backendService.acceptStrategy(String(strategy._id), {
                replace_existing: replaceExisting,
            });
            if (result?.strategy) {
                setActiveStrategy(result.strategy);
                if (result.plan_id) setCurrentPlanId(result.plan_id);
                // Refresh history so statuses update (newly-archived old
                // strategy + the now-active one).
                try {
                    const list = await backendService.fetchAllStrategies();
                    setAllStrategies(list);
                } catch {}
                // Bump PlanEditor so it refetches.
                setPlanEditorKey(k => k + 1);
                setTab('current');
            }
        } catch (err) {
            console.error('Failed to restore strategy:', err);
            window.alert(`Sorry — restoring this strategy failed: ${err.message || 'unknown error'}.`);
        }
        setRestoringStrategyId(null);
    }, [backendService, currentPlanId, inDojo]);

    // ─── Permanently delete a past strategy ───
    // The active strategy cannot be deleted from here — restore another or
    // generate a new one first.
    const [deletingStrategyId, setDeletingStrategyId] = useState(null);
    const deleteStrategy = useCallback(async (strategy) => {
        if (!strategy?._id) return;
        if (strategy.status === 'active') {
            window.alert('You can\u2019t delete the active strategy. Restore another one or generate a new one first.');
            return;
        }
        const goal = strategy.data?.primary_goal || 'this strategy';
        const confirmed = window.confirm(
            `Permanently delete "${goal}"?\n\nThis cannot be undone. Your workouts and plans are not affected.`
        );
        if (!confirmed) return;
        setDeletingStrategyId(String(strategy._id));
        try {
            const result = await backendService.deleteStrategy(String(strategy._id));
            if (!result?.ok) {
                window.alert(`Sorry — deleting this strategy failed: ${result?.error || 'unknown error'}.`);
            } else {
                setAllStrategies(prev => prev.filter(s => String(s._id) !== String(strategy._id)));
            }
        } catch (err) {
            console.error('Failed to delete strategy:', err);
            window.alert(`Sorry — deleting this strategy failed: ${err.message || 'unknown error'}.`);
        }
        setDeletingStrategyId(null);
    }, [backendService]);

    // Toggle the inline read-only detail view for an archived plan.
    // Lazily fetches entries on first expand and caches them so re-toggling
    // is instantaneous.
    const toggleArchivedPlanDetails = useCallback(async (planId) => {
        if (expandedArchivedPlanId === planId) {
            setExpandedArchivedPlanId(null);
            return;
        }
        setExpandedArchivedPlanId(planId);
        if (archivedPlanDetails[planId]?.entries) return;
        setArchivedPlanDetails(prev => ({ ...prev, [planId]: { loading: true } }));
        try {
            const entries = await backendService.fetchPlanItems(planId);
            setArchivedPlanDetails(prev => ({
                ...prev,
                [planId]: { loading: false, entries: Array.isArray(entries) ? entries : [] }
            }));
        } catch (err) {
            console.error('Failed to fetch archived plan entries:', err);
            setArchivedPlanDetails(prev => ({
                ...prev,
                [planId]: { loading: false, error: 'Could not load this plan.' }
            }));
        }
    }, [backendService, expandedArchivedPlanId, archivedPlanDetails]);

    const getCurrentPhaseIndex = () => {
        return getStrategyTimelinePresentation(activeStrategy).currentPhaseIndex;
    };

    // Get today's session from workout plan
    const getTodaysSession = (strategy) => {
        const plan = strategy?.data?.workout_plan;
        if (!plan?.sessions?.length) return null;
        if (currentPlanId && !planEntriesResolved) return null;
        const canonicalEntry = getCanonicalWorkoutForToday(currentPlanEntries);
        if (currentPlanId && !canonicalEntry) return null;
        if (canonicalEntry) {
            const canonicalDay = getCanonicalWorkoutWeekday(currentPlanEntries, canonicalEntry);
            const matchingStrategySession = plan.sessions.find(session => session.day?.toLowerCase() === canonicalDay?.toLowerCase());
            return matchingStrategySession
                ? { ...matchingStrategySession, name: canonicalEntry.activity_name || canonicalEntry.name || matchingStrategySession.name }
                : { name: canonicalEntry.activity_name || canonicalEntry.name || 'Scheduled workout', exercises: [] };
        }
        const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        return plan.sessions.find(s => s.day?.toLowerCase() === today.toLowerCase()) || null;
    };

    // Suggestion state for dojo members
    const [suggestionInput, setSuggestionInput] = useState('');
    const [suggestionSending, setSuggestionSending] = useState(false);
    const [suggestionSent, setSuggestionSent] = useState(false);

    // Chief suggestion viewing state
    const [chiefSuggestions, setChiefSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Load suggestions for chief when strategy has dojo_id
    useEffect(() => {
        if (!activeStrategy?.dojo_id || activeStrategy._isDojoStrategy) return;
        backendService.getStrategySuggestions(
            activeStrategy.dojo_id, activeStrategy._id
        ).then(data => {
            if (data?.suggestions) setChiefSuggestions(data.suggestions);
        }).catch(() => {});
    }, [activeStrategy, backendService]);

    const submitSuggestion = async () => {
        if (!suggestionInput.trim() || !activeStrategy?._dojoId) return;
        setSuggestionSending(true);
        try {
            await backendService.submitStrategySuggestion(
                activeStrategy._dojoId,
                activeStrategy._id,
                suggestionInput.trim()
            );
            setSuggestionSent(true);
            setSuggestionInput('');
            setTimeout(() => setSuggestionSent(false), 3000);
        } catch (err) {
            console.error('Failed to submit suggestion:', err);
        } finally {
            setSuggestionSending(false);
        }
    };

    const sendComposer = useCoachComposerSubmit(sendMessage, sending);
    const reviewComposer = useCoachComposerSubmit(requestReview, reviewing);
    const suggestionComposer = useCoachComposerSubmit(submitSuggestion, suggestionSending);

    const handleSuggestionAction = async (suggestionId, status) => {
        if (!activeStrategy?.dojo_id) return;
        try {
            await backendService.updateSuggestionStatus(
                activeStrategy.dojo_id, suggestionId, status
            );
            setChiefSuggestions(prev => prev.map(s =>
                s._id === suggestionId ? { ...s, status } : s
            ));
        } catch (err) {
            console.error('Failed to update suggestion:', err);
        }
    };

    // ─── RENDER ───

    // Determine if viewing a shared dojo strategy (read-only for members)
    const { inDojoContext, readOnlyStrategy: isDojoStrategy } = getStrategyContextState({
        strategy: activeStrategy,
        inDojo,
        readOnly: dojoPlanReadOnly,
    });
    const dojoName = activeStrategy?._dojoName || dojoAuthorityName;
    const dojoAuthorityNotice = isDojoStrategy ? (
        <div className="update-plan-readonly-notice strategy-dojo-authority-notice">
            {authorityError
                ? '🔒 We could not verify strategy ownership. Editing is disabled until dojo authority can be confirmed.'
                : <>🔒 Strategy and plan are established by {dojoName || 'your dojo'}. Only the dojo chief can change them. {!embedded && <button type="button" className="strategy-authority-link" onClick={openDojoSettings}>View dojo settings</button>}</>}
        </div>
    ) : null;

    if (loading || authorityLoading) {
        return (
            <div className="strategy-content">
                <div className="strategy-header">
                    <div className="strategy-header-icon">🎯</div>
                    <h2>Strategy & Plan</h2>
                </div>
                {embedded ? <p role="status">Loading strategy…</p> : <ModalLoading text="Loading strategy..." />}
            </div>
        );
    }

    if (loadError) return <div className="strategy-content"><h2>Strategy & Plan</h2><p className="dojo-settings-error" role="alert">{loadError}</p>{dojoAuthorityNotice}</div>;

    // ─── Negotiation Chat View ───
    if (negotiating) {
        return (
            <div className="strategy-content strategy-content--negotiating">
                <div className="strategy-header">
                    <div className="strategy-header-icon">🎯</div>
                    <h2>Build Your Strategy</h2>
                    <div className="strategy-subtitle">Negotiate your training strategy and plan with your AI coach</div>
                </div>

                {/*
                 * Single scrollable region containing the chat history AND the
                 * live strategy preview. The user scrolls one stream — chat at
                 * top, preview below — so neither section starves the other.
                 * Only the input row + action buttons stay pinned at the bottom.
                 */}
                <div className="strategy-scroll">
                    <div className="strategy-chat">
                        {messages.map((msg, i) => (
                            <div key={i} className={`strategy-message ${msg.role}`}>
                                {msg.text}
                            </div>
                        ))}
                        {sending && (
                            <div className="strategy-message ai strategy-progress-message">
                                <div className="strategy-progress-header">
                                    <span className="strategy-progress-spark">✨</span>
                                    <span>{strategyGenerationProgressLabel()}</span>
                                </div>
                                <div className="strategy-progress-timeline">
                                    {progressSteps.length === 0 && (
                                        <div className="strategy-progress-step running">
                                            <span className="strategy-progress-icon">
                                                <span className="strategy-progress-spinner" />
                                            </span>
                                            <span className="strategy-progress-label">Warming up…</span>
                                        </div>
                                    )}
                                    {progressSteps.map((step, i) => (
                                        <div key={`${step.id}-${i}`} className={`strategy-progress-step ${step.status}`}>
                                            <span className="strategy-progress-icon">
                                                {step.status === 'done' ? '✓' : <span className="strategy-progress-spinner" />}
                                            </span>
                                            <span className="strategy-progress-label">{step.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    {/* ─── Live Strategy Preview ───
                        Renders the proposed strategy below the chat as soon as the AI
                        has produced one, so the user can review the full plan before
                        accepting. Updates in place on every iteration. */}
                    {draftStrategy?.data?.primary_goal && (
                        <div className="strategy-preview" key={String(draftStrategy._id || draftStrategy.conversation_id || 'draft')}>
                            <div className="strategy-preview-banner">
                                <span className="strategy-preview-label">PROPOSED STRATEGY — REVIEW BEFORE ACCEPTING</span>
                            </div>
                            {renderStrategyOverview(draftStrategy, -1, null)}
                            {renderWorkoutPlan(draftStrategy)}
                            {renderNutritionPlan(draftStrategy)}
                        </div>
                    )}
                </div>

                <div className="strategy-input-row">
                    <CoachMessageInput
                        className="strategy-input"
                        placeholder="Suggest changes or ask questions..."
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onSend={sendComposer}
                        disabled={sending}
                    />
                    <button 
                        className="strategy-send-btn"
                        onClick={sendComposer}
                        disabled={sending || !inputValue.trim()}
                    >
                        Send
                    </button>
                </div>

                <div className="strategy-actions">
                    {(() => {
                        const d = draftStrategy?.data;
                        const hasStrategy = Boolean(d && (d.primary_goal || d.summary) && d.workout_plan?.sessions?.length);
                        return (
                            <button
                                className="strategy-btn primary"
                                onClick={acceptStrategy}
                                disabled={sending || !hasStrategy}
                                title={hasStrategy ? 'Activate this strategy and plan' : 'Waiting for the AI to finish proposing a strategy…'}
                            >
                                {sending ? 'Generating…' : (hasStrategy ? '✓ Accept Strategy & Plan' : '⏳ Waiting for proposal…')}
                            </button>
                        );
                    })()}
                    <button className="strategy-btn secondary" onClick={() => { setNegotiating(false); setMessages([]); setProgressSteps([]); }}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    // ─── Strategy Detail View (viewing a specific strategy from history) ───
    if (viewingStrategy) {
        return (
            <div className="strategy-content">
                <div className="strategy-header">
                    <div className="strategy-header-icon">📋</div>
                    <h2>Strategy Detail</h2>
                </div>
                {renderStrategyOverview(viewingStrategy, -1, null)}
                {renderWorkoutPlan(viewingStrategy)}
                {renderNutritionPlan(viewingStrategy)}
                <div className="strategy-actions">
                    <button className="strategy-btn secondary" onClick={() => setViewingStrategy(null)}>
                        ← Back to History
                    </button>
                </div>
            </div>
        );
    }

    // ─── Active Strategy View (with tabs) ───
    if (activeStrategy) {
        const currentPhaseIdx = getCurrentPhaseIndex();
        const todaysSession = getTodaysSession(activeStrategy);
        
        return (
            <div className="strategy-content">
                <div className="strategy-header">
                    <div className="strategy-header-icon">🎯</div>
                    <h2>Strategy & Plan</h2>
                    {isDojoStrategy && (
                        <div className="strategy-dojo-badge">🥋 {dojoName || 'Dojo'} Strategy</div>
                    )}
                </div>
                {dojoAuthorityNotice}

                <div className="modal-tabs">
                    <button className={`modal-tab ${tab === 'current' ? 'active' : ''}`} onClick={() => setTab('current')}>
                        Overview
                    </button>
                    {!isDojoStrategy && (
                        <button className={`modal-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
                            History
                        </button>
                    )}
                </div>

                {/* ─── Overview Tab ─── */}
                {tab === 'current' && (
                    <>
                        {/* Today's Session Highlight */}
                        {todaysSession && (
                            <div className="plan-today-highlight">
                                <div className="plan-today-label">📅 Today's Session</div>
                                <div className="plan-today-name">{todaysSession.name}</div>
                                <div className="plan-today-exercises">
                                    {(todaysSession.exercises || []).map((ex, i) => (
                                        <span key={i} className="plan-today-exercise">{ex.name}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {renderStrategyOverview(activeStrategy, currentPhaseIdx, null)}

                        {!isDojoStrategy && (
                            <PhaseTransitionBanner
                                strategy={activeStrategy}
                                currentPhaseIdx={currentPhaseIdx}
                                regenerating={regeneratingPlan}
                                onAdvance={regeneratePlan}
                                hasPlan={!!currentPlanId}
                            />
                        )}

                        {/* Review Section */}
                        {reviewing && !reviewResult && (
                            <div className="strategy-review">
                                <div className="strategy-review-title">📊 Reviewing your strategy…</div>
                                <div className="strategy-review-text" style={{ opacity: 0.7 }}>
                                    Looking at your recent workouts, nutrition, and progress. This usually takes 10–20 seconds.
                                </div>
                            </div>
                        )}
                        {reviewResult && (
                            <div className="strategy-review">
                                <div className="strategy-review-title">📊 Coach</div>
                                <div className="strategy-review-text" style={{ whiteSpace: 'pre-wrap' }}>
                                    {reviewResult.ai_message || reviewResult.assessment}
                                </div>
                                {Array.isArray(reviewResult.adjustments) && reviewResult.adjustments.length > 0 && (
                                    <ul className="strategy-review-text" style={{ marginTop: 8, paddingLeft: 18 }}>
                                        {reviewResult.adjustments.map((a, i) => (
                                            <li key={i}>{a}</li>
                                        ))}
                                    </ul>
                                )}
                                {reviewResult.recommendation && (
                                    <div className="strategy-review-text" style={{ marginTop: 8, color: '#a78bfa' }}>
                                        Recommendation: {reviewResult.recommendation}
                                    </div>
                                )}
                                {renderPendingPlanChanges(
                                    reviewResult.pending_plan_changes,
                                    pendingChangesState,
                                    setPendingChangesState,
                                    backendService,
                                    setReviewResult,
                                    regeneratePlan
                                )}
                            </div>
                        )}

                        {/* Chief: Member Suggestions */}
                        {!isDojoStrategy && chiefSuggestions.length > 0 && (
                            <div className="strategy-suggestions">
                                <div 
                                    className="strategy-suggestions-header"
                                    onClick={() => setShowSuggestions(prev => !prev)}
                                >
                                    <span className="strategy-suggestions-title">
                                        💬 Member Suggestions
                                        {chiefSuggestions.filter(s => s.status === 'pending').length > 0 && (
                                            <span className="strategy-suggestions-badge">
                                                {chiefSuggestions.filter(s => s.status === 'pending').length}
                                            </span>
                                        )}
                                    </span>
                                    <span className={`strategy-suggestions-chevron ${showSuggestions ? 'expanded' : ''}`}>‹</span>
                                </div>
                                {showSuggestions && (
                                    <div className="strategy-suggestions-list">
                                        {chiefSuggestions.map(s => (
                                            <div key={s._id} className={`strategy-suggestion ${s.status}`}>
                                                <div className="strategy-suggestion-meta">
                                                    <span className="strategy-suggestion-author">{s.username}</span>
                                                    <span className="strategy-suggestion-time">
                                                        {new Date(s.created_at).toLocaleDateString()}
                                                    </span>
                                                    {s.status !== 'pending' && (
                                                        <span className={`strategy-suggestion-status ${s.status}`}>
                                                            {s.status}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="strategy-suggestion-text">{s.text}</div>
                                                {s.status === 'pending' && (
                                                    <div className="strategy-suggestion-actions">
                                                        <button
                                                            className="strategy-suggestion-btn acknowledge"
                                                            onClick={() => handleSuggestionAction(s._id, 'acknowledged')}
                                                        >
                                                            👍
                                                        </button>
                                                        <button
                                                            className="strategy-suggestion-btn dismiss"
                                                            onClick={() => handleSuggestionAction(s._id, 'dismissed')}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Review Input — chief/independent only */}
                        {!isDojoStrategy && (
                            <div className="strategy-input-row" style={{ marginTop: reviewResult ? 12 : 20 }}>
                                <CoachMessageInput
                                    className="strategy-input"
                                    placeholder="Ask about your progress or request a review..."
                                    value={reviewInput}
                                    onChange={e => setReviewInput(e.target.value)}
                                    onSend={reviewComposer}
                                    disabled={reviewing}
                                />
                                <button 
                                    className="strategy-send-btn"
                                    onClick={reviewComposer}
                                    disabled={reviewing}
                                >
                                    {reviewing ? '...' : 'Review'}
                                </button>
                            </div>
                        )}

                        {/* Suggestion Input — dojo members */}
                        {isDojoStrategy && (
                            <div className="strategy-input-row" style={{ marginTop: 20 }}>
                                <CoachMessageInput
                                    className="strategy-input"
                                    placeholder="Suggest a change to the strategy..."
                                    value={suggestionInput}
                                    onChange={e => setSuggestionInput(e.target.value)}
                                    onSend={suggestionComposer}
                                    disabled={suggestionSending}
                                    maxLength={1000}
                                />
                                <button
                                    className="strategy-send-btn"
                                    onClick={suggestionComposer}
                                    disabled={suggestionSending || !suggestionInput.trim()}
                                >
                                    {suggestionSending ? '...' : suggestionSent ? '✓' : 'Suggest'}
                                </button>
                            </div>
                        )}

                        {!isDojoStrategy && (
                            <div className="strategy-actions">
                                <button className="strategy-btn primary" onClick={startNewStrategy}>
                                    🔄 New Strategy
                                </button>
                                <button className="strategy-btn danger" onClick={archiveStrategy}>
                                    Archive
                                </button>
                            </div>
                        )}

                        {/* ─── Plan Subsection ─── */}
                        <div className="strategy-plan-section">
                            <div className="strategy-plan-section-divider" />
                            <h3 className="strategy-plan-section-title">📋 Plan</h3>

                            <RestDaysSettings
                                backendService={backendService}
                                dojoContext={inDojoContext}
                            />
                            <TravelReplacementSettings
                                backendService={backendService}
                                dojoContext={inDojoContext}
                            />

                            {currentPlanId && !isDojoStrategy && (
                                <div className="strategy-actions">
                                    <button
                                        className="strategy-btn secondary"
                                        onClick={regeneratePlan}
                                        disabled={regeneratingPlan}
                                    >
                                        {regeneratingPlan ? 'Regenerating...' : '🔄 Regenerate workouts for current phase'}
                                    </button>
                                </div>
                            )}

                            {/*
                             * The strategy's intended workout plan and nutrition
                             * macros used to be re-rendered here, but every
                             * scheduled session/meal is already shown by
                             * <PlanEditor> below — and the macro targets now
                             * live in the strategy overview at the top. Keeping
                             * this section focused on the actual plan items.
                             */}

                            {/* Archived plans now live in the History tab. */}

                            {/* Editable Activity Plan */}
                            {currentPlanId && (
                                <>
                                    {dojoPlanReadOnly && <div className="update-plan-readonly-notice">🔒 This plan is shared by the dojo. Only the dojo leader can change the workout plan.</div>}
                                    <PlanEditor
                                        key={`plan-editor-${currentPlanId}-${planEditorKey}`}
                                        planId={currentPlanId}
                                        backendService={backendService}
                                        modalService={modalService}
                                        onClose={close}
                                        readOnly={dojoPlanReadOnly}
                                        showLoadingCue={false}
                                        onInitialReady={markInitialPlanReady}
                                    />
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* ─── History Tab ─── */}
                {tab === 'history' && (
                    <>
                        {/* Past strategies */}
                        <h4 className="strategy-history-section-title">📜 Previous Strategies</h4>
                        {allStrategies.length === 0 ? (
                            <div className="strategy-empty">
                                <div className="strategy-empty-text">No previous strategies yet.</div>
                            </div>
                        ) : (
                            <div className="strategy-archived-plans-list">
                                {allStrategies.map(s => {
                                    const meta = [
                                        new Date(s.created_at).toLocaleDateString(),
                                        s.data?.timeline_weeks ? `${s.data.timeline_weeks} weeks` : null,
                                        s.data?.workout_plan?.days_per_week ? `${s.data.workout_plan.days_per_week} days/wk` : null,
                                    ].filter(Boolean).join(' · ');
                                    const isActive = s.status === 'active';
                                    return (
                                        <div key={String(s._id)} className={`strategy-archived-plan${isActive ? ' active' : ''}`}>
                                            {isActive && (
                                                <div className="strategy-archived-plan-active-banner">
                                                    ● Currently active
                                                </div>
                                            )}
                                            <div className="strategy-archived-plan-row">
                                                <div className="strategy-archived-plan-info">
                                                    <div className="strategy-archived-plan-title">
                                                        {s.data?.primary_goal || 'Untitled Strategy'}
                                                        <span className={`strategy-status ${s.status}`} style={{ marginLeft: 8, fontSize: 10 }}>
                                                            {s.status}
                                                        </span>
                                                    </div>
                                                    <div className="strategy-archived-plan-meta">{meta}</div>
                                                    {s.data?.summary && (
                                                        <div className="strategy-archived-plan-goal">{s.data.summary}</div>
                                                    )}
                                                </div>
                                                <div className="strategy-archived-plan-actions">
                                                    <button
                                                        className="strategy-btn secondary strategy-archived-plan-btn"
                                                        onClick={() => setViewingStrategy(s)}
                                                        title="See the full strategy"
                                                    >
                                                        👁 View
                                                    </button>
                                                    {s.status !== 'active' && (
                                                        <button
                                                            className="strategy-btn secondary strategy-archived-plan-btn"
                                                            onClick={() => restoreStrategy(s)}
                                                            disabled={restoringStrategyId === String(s._id)}
                                                            title="Reactivate this strategy and rebuild your plan from it"
                                                        >
                                                            {restoringStrategyId === String(s._id) ? 'Restoring…' : '↺ Restore'}
                                                        </button>
                                                    )}
                                                    {s.status !== 'active' && (
                                                        <button
                                                            className="strategy-btn secondary strategy-archived-plan-btn danger"
                                                            onClick={() => deleteStrategy(s)}
                                                            disabled={deletingStrategyId === String(s._id)}
                                                            title="Permanently delete this strategy"
                                                        >
                                                            {deletingStrategyId === String(s._id) ? 'Deleting…' : '🗑 Delete'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Plans section — surfaces the currently active plan
                            (so users see it gets the same treatment as the
                            active strategy) plus all archived snapshots. */}
                        {!isDojoStrategy && (activePlanSummary || archivedPlans.length > 0) && (
                            <div className="strategy-archived-plans" style={{ marginTop: 24 }}>
                                <h4 className="strategy-history-section-title">📦 Plans</h4>
                                {archivedPlans.length > 0 && (
                                    <div className="strategy-archived-plans-subtitle">
                                        Your old workouts are saved here whenever a new strategy replaces them. Restore one any time.
                                    </div>
                                )}
                                <div className="strategy-archived-plans-list">
                                    {/* Currently-active plan card */}
                                    {activePlanSummary && (() => {
                                        const planIdStr = String(activePlanSummary._id);
                                        const isExpanded = expandedArchivedPlanId === planIdStr;
                                        const detail = archivedPlanDetails[planIdStr];
                                        return (
                                            <div key={planIdStr} className="strategy-archived-plan active">
                                                <div className="strategy-archived-plan-active-banner">● Currently active</div>
                                                <div className="strategy-archived-plan-row">
                                                    <div className="strategy-archived-plan-info">
                                                        <div className="strategy-archived-plan-title">
                                                            {activePlanSummary.title || 'Training Plan'}
                                                        </div>
                                                        {activePlanSummary.goal_statement && (
                                                            <div className="strategy-archived-plan-goal">{activePlanSummary.goal_statement}</div>
                                                        )}
                                                    </div>
                                                    <div className="strategy-archived-plan-actions">
                                                        <button
                                                            className="strategy-btn secondary strategy-archived-plan-btn"
                                                            onClick={() => toggleArchivedPlanDetails(planIdStr)}
                                                            title={isExpanded ? 'Hide plan details' : 'See what is in this plan'}
                                                        >
                                                            {isExpanded ? '▴ Hide' : '👁 View'}
                                                        </button>
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className="strategy-archived-plan-details">
                                                        {detail?.loading && (
                                                            <div className="strategy-archived-plan-detail-loading">Loading plan details…</div>
                                                        )}
                                                        {detail?.error && (
                                                            <div className="strategy-archived-plan-detail-error">{detail.error}</div>
                                                        )}
                                                        {detail?.entries && (
                                                            <ArchivedPlanSummary entries={detail.entries} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {archivedPlans.map(p => {
                                        const planIdStr = String(p._id);
                                        const isExpanded = expandedArchivedPlanId === planIdStr;
                                        const detail = archivedPlanDetails[planIdStr];
                                        return (
                                            <div key={planIdStr} className="strategy-archived-plan">
                                                <div className="strategy-archived-plan-row">
                                                    <div className="strategy-archived-plan-info">
                                                        <div className="strategy-archived-plan-title">
                                                            {p.title || 'Training Plan'}
                                                        </div>
                                                        <div className="strategy-archived-plan-meta">
                                                            Archived {p.archived_at ? new Date(p.archived_at).toLocaleDateString() : 'recently'}
                                                            {p.archived_reason === 'replaced_by_restore' && ' · from a previous restore'}
                                                        </div>
                                                        {p.goal_statement && (
                                                            <div className="strategy-archived-plan-goal">{p.goal_statement}</div>
                                                        )}
                                                    </div>
                                                    <div className="strategy-archived-plan-actions">
                                                        <button
                                                            className="strategy-btn secondary strategy-archived-plan-btn"
                                                            onClick={() => toggleArchivedPlanDetails(planIdStr)}
                                                            title={isExpanded ? 'Hide plan details' : 'See what was in this plan'}
                                                        >
                                                            {isExpanded ? '▴ Hide' : '👁 View'}
                                                        </button>
                                                        <button
                                                            className="strategy-btn secondary strategy-archived-plan-btn"
                                                            onClick={() => restoreArchivedPlan(planIdStr)}
                                                            disabled={restoringPlanId === planIdStr}
                                                        >
                                                            {restoringPlanId === planIdStr ? 'Restoring…' : '↺ Restore'}
                                                        </button>
                                                        <button
                                                            className="strategy-btn danger strategy-archived-plan-btn"
                                                            onClick={() => deleteArchivedPlan(planIdStr)}
                                                            title="Permanently delete this saved plan"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className="strategy-archived-plan-details">
                                                        {detail?.loading && (
                                                            <div className="strategy-archived-plan-detail-loading">Loading plan details…</div>
                                                        )}
                                                        {detail?.error && (
                                                            <div className="strategy-archived-plan-detail-error">{detail.error}</div>
                                                        )}
                                                        {detail?.entries && (
                                                            <ArchivedPlanSummary entries={detail.entries} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="strategy-actions">
                            <button className="strategy-btn primary" onClick={startNewStrategy}>
                                + Create Strategy
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ─── No Active Strategy (Empty State) ───
    return (
        <div className="strategy-content">
            <div className="strategy-header">
                <div className="strategy-header-icon">🎯</div>
                <h2>Strategy & Plan</h2>
                {isDojoStrategy && (
                    <div className="strategy-dojo-badge">🥋 {dojoName || 'Dojo'} Strategy</div>
                )}
            </div>
            {dojoAuthorityNotice}

            <div className="modal-tabs">
                <button className={`modal-tab ${tab === 'current' ? 'active' : ''}`} onClick={() => setTab('current')}>
                    Current
                </button>
                {!isDojoStrategy && (
                    <button className={`modal-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
                        History
                    </button>
                )}
            </div>

            {tab === 'current' ? (
                <>
                    <div className="strategy-empty">
                        <div className="strategy-empty-icon">{isDojoStrategy ? '🥋' : '🗺️'}</div>
                        <div className="strategy-empty-text">
                            {isDojoStrategy
                                ? `Your dojo chief hasn't created a training strategy for ${dojoName || 'the dojo'} yet. Once they do, it will appear here automatically.`
                                : `You don't have an active strategy yet. Your AI coach will build a 
personalized strategy with phases, milestones, a concrete weekly workout plan, 
and nutrition targets — all based on your goals and training history.`
                            }
                        </div>
                        {!isDojoStrategy && (
                            <button className="strategy-btn primary" onClick={startNewStrategy}>
                                Build My Strategy
                            </button>
                        )}
                    </div>

                    {/* Plan is always accessible, even without a strategy */}
                    {currentPlanId && (
                        <div className="strategy-plan-section">
                            <div className="strategy-plan-section-divider" />
                            <h3 className="strategy-plan-section-title">📋 Plan</h3>
                            <RestDaysSettings
                                backendService={backendService}
                                dojoContext={inDojoContext}
                            />
                            <TravelReplacementSettings
                                backendService={backendService}
                                dojoContext={inDojoContext}
                            />
                            {!isDojoStrategy && (
                                <div className="strategy-actions">
                                    <button
                                        className="strategy-btn secondary"
                                        onClick={regeneratePlan}
                                        disabled={regeneratingPlan}
                                    >
                                        {regeneratingPlan ? 'Regenerating...' : '🔄 Regenerate Plan'}
                                    </button>
                                </div>
                            )}
                            {isDojoStrategy && <div className="update-plan-readonly-notice">🔒 This plan is shared by the dojo. Only the dojo chief can change it.</div>}
                            <PlanEditor
                                planId={currentPlanId}
                                backendService={backendService}
                                modalService={modalService}
                                onClose={close}
                                readOnly={dojoPlanReadOnly}
                                showLoadingCue={false}
                            />
                        </div>
                    )}
                </>
            ) : (
                <>
                    {allStrategies.length === 0 ? (
                        <div className="strategy-empty">
                            <div className="strategy-empty-text">No previous strategies yet.</div>
                        </div>
                    ) : (
                        allStrategies.map(s => (
                            <div 
                                key={String(s._id)} 
                                className="strategy-history-item"
                                onClick={() => setViewingStrategy(s)}
                            >
                                <div className="strategy-history-title">
                                    {s.data?.primary_goal || 'Untitled Strategy'}
                                    <span className={`strategy-status ${s.status}`} style={{ marginLeft: 8, fontSize: 10 }}>
                                        {s.status}
                                    </span>
                                </div>
                                <div className="strategy-history-meta">
                                    {new Date(s.created_at).toLocaleDateString()} · {s.data?.timeline_weeks ? s.data.timeline_weeks + ' weeks' : 'Unknown duration'}
                                </div>
                            </div>
                        ))
                    )}

                    <div className="strategy-actions">
                        <button className="strategy-btn primary" onClick={startNewStrategy}>
                            + Create Strategy
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

// ─── Reusable strategy overview renderer ───
function renderStrategyOverview(strategy, currentPhaseIdx, onToggleMilestone) {
    const data = strategy.data || {};
    const phases = data.phases || [];
    const milestones = data.milestones || [];
    const hasStarted = Boolean(strategy.started_at || strategy.activated_at);
    const timeline = getStrategyTimelinePresentation(strategy);
    const weeksElapsed = hasStarted ? timeline.currentWeek : null;

    return (
        <div className="strategy-active">
            <div className="strategy-title-bar">
                <h3 className="strategy-title">{data.primary_goal || 'Untitled Strategy'}</h3>
                <span className={`strategy-status ${strategy.status}`}>{strategy.status}</span>
            </div>

            {data.summary && <div className="strategy-goal">{data.summary}</div>}

            <div className="strategy-meta">
                {data.timeline_weeks && (
                    <div className="strategy-meta-item">
                        <div className="strategy-meta-label">Duration</div>
                        <div className="strategy-meta-value">{data.timeline_weeks} weeks</div>
                    </div>
                )}
                {weeksElapsed !== null && (
                    <div className="strategy-meta-item">
                        <div className="strategy-meta-label">Week</div>
                        <div className="strategy-meta-value">{timeline.weekLabel}</div>
                    </div>
                )}
                {data.workout_plan?.days_per_week && (
                    <div className="strategy-meta-item">
                        <div className="strategy-meta-label">Training</div>
                        <div className="strategy-meta-value">{data.workout_plan.days_per_week} days/wk</div>
                    </div>
                )}
                {data.review_at_weeks && (
                    <div className="strategy-meta-item">
                        <div className="strategy-meta-label">Review at</div>
                        <div className="strategy-meta-value">Week {data.review_at_weeks}</div>
                    </div>
                )}
            </div>

            {phases.length > 0 && (
                <StrategyRoadmap
                    phases={phases}
                    currentPhaseIndex={currentPhaseIdx}
                    weeksElapsed={weeksElapsed}
                />
            )}

            {milestones.length > 0 && (
                <div className="strategy-milestones">
                    <div className="strategy-milestones-title">Milestones</div>
                    <div className="strategy-milestones-note">
                        Phase milestones are part of your strategy and plan. Check them off when you have verified the target.
                    </div>
                    {milestones.map((m, i) => (
                        <div 
                            key={i} 
                            className={`strategy-milestone ${onToggleMilestone ? 'interactive' : ''}`}
                            onClick={onToggleMilestone ? () => onToggleMilestone(i, m.done) : undefined}
                        >
                            <div className={`strategy-milestone-check ${m.done ? 'done' : ''}`}>
                                {m.done ? '✓' : ''}
                            </div>
                            <span>{typeof m === 'string' ? m : m.description || m.name || JSON.stringify(m)}</span>

                            {m.target_date_offset_weeks && (
                                <span className="strategy-milestone-week">Wk {m.target_date_offset_weeks}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/*
             * Nutrition targets are part of the strategy, not the schedule.
             * Render them inline with the overview so the Plan subsection
             * below stays focused on the actual scheduled entries.
             */}
            {renderNutritionTargets(data.nutrition_plan)}
        </div>
    );
}

// ─── Workout Plan renderer ───
function renderWorkoutPlan(strategy) {
    const plan = strategy?.data?.workout_plan;
    if (!plan?.sessions?.length) return null;

    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

    return (
        <div className="plan-section">
            <div className="plan-section-title">
                🏋️ Weekly Workout Plan
                <span className="plan-days-badge">{plan.days_per_week} days/wk</span>
            </div>

            <div className="plan-sessions">
                {plan.sessions.map((session, i) => {
                    const isToday = session.day?.toLowerCase() === today.toLowerCase();
                    return (
                        <div key={i} className={`plan-session ${isToday ? 'today' : ''}`}>
                            <div className="plan-session-header">
                                <span className="plan-session-day">{session.day}</span>
                                {isToday && <span className="plan-session-today-badge">Today</span>}
                                <span className="plan-session-name">{session.name}</span>
                            </div>
                            <div className="plan-session-exercises">
                                {(session.exercises || []).map((ex, j) => (
                                    <div key={j} className="plan-exercise">
                                        <span className="plan-exercise-name">{ex.name}</span>
                                        <span className="plan-exercise-prescription">
                                            {ex.sets && ex.reps ? `${ex.sets} × ${ex.reps}` : ''}
                                        </span>
                                        {ex.notes && <span className="plan-exercise-notes">{ex.notes}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {plan.progression_rules?.length > 0 && (
                <div className="plan-rules">
                    <div className="plan-rules-title">📈 Progression</div>
                    {plan.progression_rules.map((rule, i) => (
                        <div key={i} className="plan-rule">• {rule}</div>
                    ))}
                </div>
            )}

            {plan.deload_protocol && (
                <div className="plan-deload">
                    <div className="plan-rules-title">🔄 Deload</div>
                    <div className="plan-rule">{plan.deload_protocol}</div>
                </div>
            )}
        </div>
    );
}

// ─── Nutrition targets (compact, for the strategy overview) ───
// Just the macro targets + guidelines — no "Nutrition Plan" framing,
// since the actual meals are scheduled in the Plan subsection below.
function renderNutritionTargets(plan) {
    if (!plan) return null;
    const hasMacros = plan.daily_calories || plan.protein_g || plan.carbs_g || plan.fat_g || plan.water_ml;
    const hasGuidelines = plan.guidelines?.length > 0;
    if (!hasMacros && !hasGuidelines) return null;

    return (
        <div className="strategy-nutrition-targets">
            <div className="strategy-nutrition-targets-title">🥗 Daily Nutrition Targets</div>
            {hasMacros && (
                <div className="plan-macros">
                    {plan.daily_calories && (
                        <div className="plan-macro">
                            <div className="plan-macro-value">{plan.daily_calories}</div>
                            <div className="plan-macro-label">Calories</div>
                        </div>
                    )}
                    {plan.protein_g && (
                        <div className="plan-macro protein">
                            <div className="plan-macro-value">{plan.protein_g}g</div>
                            <div className="plan-macro-label">Protein</div>
                        </div>
                    )}
                    {plan.carbs_g && (
                        <div className="plan-macro carbs">
                            <div className="plan-macro-value">{plan.carbs_g}g</div>
                            <div className="plan-macro-label">Carbs</div>
                        </div>
                    )}
                    {plan.fat_g && (
                        <div className="plan-macro fat">
                            <div className="plan-macro-value">{plan.fat_g}g</div>
                            <div className="plan-macro-label">Fat</div>
                        </div>
                    )}
                    {plan.water_ml && (
                        <div className="plan-macro">
                            <div className="plan-macro-value">{plan.water_ml}ml</div>
                            <div className="plan-macro-label">Water</div>
                        </div>
                    )}
                </div>
            )}
            {hasGuidelines && (
                <div className="plan-guidelines">
                    {plan.guidelines.map((g, i) => (
                        <div key={i} className="plan-guideline">• {g}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Nutrition Plan renderer (full, used in draft preview / history detail) ───
function renderNutritionPlan(strategy) {
    const plan = strategy?.data?.nutrition_plan;
    if (!plan) return null;

    const hasMacros = plan.daily_calories || plan.protein_g;
    const hasGuidelines = plan.guidelines?.length > 0;
    if (!hasMacros && !hasGuidelines) return null;

    return (
        <div className="plan-section">
            <div className="plan-section-title">🥗 Nutrition Plan</div>

            {hasMacros && (
                <div className="plan-macros">
                    {plan.daily_calories && (
                        <div className="plan-macro">
                            <div className="plan-macro-value">{plan.daily_calories}</div>
                            <div className="plan-macro-label">Calories</div>
                        </div>
                    )}
                    {plan.protein_g && (
                        <div className="plan-macro protein">
                            <div className="plan-macro-value">{plan.protein_g}g</div>
                            <div className="plan-macro-label">Protein</div>
                        </div>
                    )}
                    {plan.carbs_g && (
                        <div className="plan-macro carbs">
                            <div className="plan-macro-value">{plan.carbs_g}g</div>
                            <div className="plan-macro-label">Carbs</div>
                        </div>
                    )}
                    {plan.fat_g && (
                        <div className="plan-macro fat">
                            <div className="plan-macro-value">{plan.fat_g}g</div>
                            <div className="plan-macro-label">Fat</div>
                        </div>
                    )}
                    {plan.water_ml && (
                        <div className="plan-macro">
                            <div className="plan-macro-value">{plan.water_ml}ml</div>
                            <div className="plan-macro-label">Water</div>
                        </div>
                    )}
                    {plan.meals_per_day && (
                        <div className="plan-macro">
                            <div className="plan-macro-value">{plan.meals_per_day}</div>
                            <div className="plan-macro-label">Meals/day</div>
                        </div>
                    )}
                </div>
            )}

            {hasGuidelines && (
                <div className="plan-guidelines">
                    {plan.guidelines.map((g, i) => (
                        <div key={i} className="plan-guideline">• {g}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default StrategyModal;

/**
 * Render the "Pending plan changes" panel under a coach review when
 * the backend deferred structural plan changes (meal additions/removals,
 * workout plan regenerate). Lets the user explicitly accept the
 * additions ("Apply"), accept everything including removals ("Replace
 * existing"), or dismiss ("Cancel"). Without this, structural changes
 * would never reach the user's plan entries.
 */
function renderPendingPlanChanges(pending, state, setState, backendService, setReviewResult, regeneratePlan) {
    if (!pending) return null;
    const nutrition = pending.nutrition;
    const workouts = pending.workouts;
    if (!nutrition && !workouts) return null;

    const nutritionApplied = state.applied_scopes.includes('nutrition');
    const workoutsApplied = state.applied_scopes.includes('workouts');

    const fmtTarget = t => (t && typeof t.calories === 'number') ? ` (${t.calories} kcal)` : '';

    const applyNutrition = async (mode) => {
        if (state.applying) return;
        setState(s => ({ ...s, applying: true, error: null }));
        try {
            if (mode === 'cancel') {
                // Just remove this scope from the review so the panel collapses.
                setReviewResult(prev => {
                    if (!prev?.pending_plan_changes) return prev;
                    const next = { ...prev.pending_plan_changes };
                    delete next.nutrition;
                    return { ...prev, pending_plan_changes: Object.keys(next).length ? next : null };
                });
            } else {
                await backendService.applyPendingPlanChanges({ scope: 'nutrition', mode });
                setState(s => ({
                    ...s,
                    applying: false,
                    applied_scopes: [...s.applied_scopes, 'nutrition']
                }));
                return;
            }
            setState(s => ({ ...s, applying: false }));
        } catch (err) {
            setState(s => ({ ...s, applying: false, error: err.message || 'Failed to apply' }));
        }
    };

    const dismissWorkouts = () => {
        setReviewResult(prev => {
            if (!prev?.pending_plan_changes) return prev;
            const next = { ...prev.pending_plan_changes };
            delete next.workouts;
            return { ...prev, pending_plan_changes: Object.keys(next).length ? next : null };
        });
    };

    return (
        <div className="strategy-pending-changes" style={{ marginTop: 12, padding: 12, border: '1px solid #4b3f6e', borderRadius: 8, background: 'rgba(167,139,250,0.06)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>⏸ Plan changes pending your approval</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>
                I've updated targets and names where it was safe to do so. The changes below will add or remove items in your plan, so I'm waiting for your OK.
            </div>

            {nutrition && !nutritionApplied && (
                <div style={{ marginBottom: 10 }}>
                    <div style={{ fontWeight: 500, marginBottom: 6 }}>🍽 Meal entries</div>
                    {Array.isArray(nutrition.additions) && nutrition.additions.length > 0 && (
                        <ul style={{ margin: '4px 0 8px 18px' }}>
                            {nutrition.additions.map((a, i) => (
                                <li key={`add-${i}`} style={{ color: '#86efac' }}>+ Add {a.name}{fmtTarget(a.target)}</li>
                            ))}
                        </ul>
                    )}
                    {Array.isArray(nutrition.removals) && nutrition.removals.length > 0 && (
                        <ul style={{ margin: '4px 0 8px 18px' }}>
                            {nutrition.removals.map((r, i) => (
                                <li key={`rem-${i}`} style={{ color: '#fca5a5' }}>− Remove {r.name}{fmtTarget(r.target)}</li>
                            ))}
                        </ul>
                    )}
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                        New entries start tomorrow in your local timezone — they won't backfill today.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {Array.isArray(nutrition.additions) && nutrition.additions.length > 0 && (
                            <button className="strategy-btn primary" disabled={state.applying} onClick={() => applyNutrition('add')}>
                                {state.applying ? 'Applying…' : 'Apply additions'}
                            </button>
                        )}
                        {Array.isArray(nutrition.removals) && nutrition.removals.length > 0 && (
                            <button className="strategy-btn primary" disabled={state.applying} onClick={() => applyNutrition('replace')}>
                                {state.applying ? 'Applying…' : 'Apply all (incl. removals)'}
                            </button>
                        )}
                        <button className="strategy-btn secondary" disabled={state.applying} onClick={() => applyNutrition('cancel')}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            {nutrition && nutritionApplied && (
                <div style={{ color: '#86efac', marginBottom: 10 }}>✓ Meal changes applied.</div>
            )}

            {workouts && !workoutsApplied && (
                <div style={{ marginBottom: 4 }}>
                    <div style={{ fontWeight: 500, marginBottom: 6 }}>🏋️ Workout plan</div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 8 }}>
                        Your workout schedule changed. Regenerate the plan to materialize the new sessions, or dismiss to keep your current workouts.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="strategy-btn primary" onClick={() => { dismissWorkouts(); regeneratePlan(); }}>
                            Regenerate plan
                        </button>
                        <button className="strategy-btn secondary" onClick={dismissWorkouts}>
                            Keep current
                        </button>
                    </div>
                </div>
            )}

            {state.error && (
                <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 6 }}>Couldn't apply: {state.error}</div>
            )}
        </div>
    );
}


// ─── Read-only summary of an archived plan ───
// Groups entries by their instruction type so users can quickly see what
// was scheduled (workouts / meals / metrics / media / surveys) without
// loading the full PlanEditor.
function ArchivedPlanSummary({ entries }) {
    if (!entries || entries.length === 0) {
        return <div className="strategy-archived-plan-detail-empty">This plan had no entries.</div>;
    }

    const GROUP_BY_INSTRUCTION = {
        do_workout_generated_manually: { label: 'Workouts', icon: '🏋️' },
        do_workout_generated_automatically_in_advance: { label: 'Workouts', icon: '🏋️' },
        do_workout_regenerated_every_time: { label: 'Workouts', icon: '🏋️' },
        eat_meal: { label: 'Meals', icon: '🥗' },
        upload_media: { label: 'Media check-ins', icon: '📸' },
        take_measurements: { label: 'Measurements', icon: '📏' },
        take_survey: { label: 'Surveys', icon: '📝' },
    };
    const ORDERED_GROUPS = ['Workouts', 'Meals', 'Media check-ins', 'Measurements', 'Surveys', 'Other'];

    const groups = {};
    for (const entry of entries) {
        const meta = GROUP_BY_INSTRUCTION[entry.instruction] || { label: 'Other', icon: '•' };
        if (!groups[meta.label]) groups[meta.label] = { icon: meta.icon, items: [] };
        groups[meta.label].items.push(entry);
    }

    const formatRecurrence = (entry) => {
        const n = entry.recurrence_number;
        const u = entry.recurrence_unit;
        if (!n || !u) return '';
        if (n === 1 && u === 'days') return 'Daily';
        if (n === 1 && u === 'weeks') return 'Weekly';
        if (n === 1 && u === 'months') return 'Monthly';
        return `Every ${n} ${u}`;
    };

    return (
        <div className="archived-plan-summary">
            {ORDERED_GROUPS.filter(g => groups[g]).map(label => {
                const group = groups[label];
                return (
                    <div key={label} className="archived-plan-summary-group">
                        <div className="archived-plan-summary-group-title">
                            <span className="archived-plan-summary-icon">{group.icon}</span>
                            {label}
                            <span className="archived-plan-summary-count">{group.items.length}</span>
                        </div>
                        <ul className="archived-plan-summary-list">
                            {group.items.map((entry) => {
                                const name = entry.activity_name
                                    || entry.autogeneration_hint
                                    || 'Untitled';
                                const cadence = formatRecurrence(entry);
                                return (
                                    <li key={String(entry._id)} className="archived-plan-summary-item">
                                        <span className="archived-plan-summary-item-name">{name}</span>
                                        {cadence && (
                                            <span className="archived-plan-summary-item-cadence">{cadence}</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Phase transition banner ───
// When the user is in the final week of a phase and another phase follows,
// surface a one-click prompt to refresh their workouts for the next phase.
// The banner is silent in all other cases.
function PhaseTransitionBanner({ strategy, currentPhaseIdx, regenerating, onAdvance, hasPlan }) {
    if (!hasPlan) return null;
    const phases = strategy?.data?.phases || [];
    const current = phases[currentPhaseIdx];
    const next = phases[currentPhaseIdx + 1];
    if (!current || !next || !strategy.activated_at) return null;

    const match = (current.weeks || '').match(/(\d+)\s*-\s*(\d+)/);
    if (!match) return null;
    const endWeek = parseInt(match[2], 10);
    const weeksElapsed = Math.floor(
        (Date.now() - new Date(strategy.activated_at).getTime()) / (7 * 24 * 60 * 60 * 1000)
    ) + 1;
    const weeksRemaining = endWeek - weeksElapsed;
    if (weeksRemaining > 1) return null; // only show in the last week (or past)

    const isPast = weeksRemaining < 0;
    const headline = isPast
        ? `You've finished Phase ${currentPhaseIdx + 1}: ${current.name}.`
        : `You're wrapping up Phase ${currentPhaseIdx + 1}: ${current.name} this week.`;

    return (
        <div className="phase-transition-banner">
            <div className="phase-transition-text">
                <div className="phase-transition-headline">{headline}</div>
                <div className="phase-transition-sub">
                    Ready to advance to <strong>Phase {currentPhaseIdx + 2}: {next.name}</strong>?
                    {next.training_focus && <span className="phase-transition-focus"> — {next.training_focus}</span>}
                </div>
            </div>
            <button
                className="strategy-btn primary phase-transition-btn"
                onClick={onAdvance}
                disabled={regenerating}
            >
                {regenerating ? 'Refreshing…' : '🚀 Refresh workouts for the new phase'}
            </button>
        </div>
    );
}
